import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminReelForm from './AdminReelForm';

const AdminReelManager = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [isPublishing, setIsPublishing] = useState(null); // Track which item is publishing
    const [isUploadingInstagram, setIsUploadingInstagram] = useState(null); // Track which item is uploading to Instagram
    const [showInstaModal, setShowInstaModal] = useState(false);
    const [instaCaption, setInstaCaption] = useState('');
    const [instaTargetId, setInstaTargetId] = useState(null);
    const [notification, setNotification] = useState(null);

    const fetchItems = async () => {
        setIsLoading(true);
        const token = localStorage.getItem('app_token');
        const response = await axios.get('/api/admin/reels', { headers: { Authorization: `Bearer ${token}` } });
        setItems(response.data);
        setIsLoading(false);
    };

    useEffect(() => { fetchItems(); }, []);

    const handleSave = async (formData) => {
        const token = localStorage.getItem('app_token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const url = editingItemId ? `/api/admin/reels/${editingItemId}` : '/api/admin/reels';
        const method = editingItemId ? 'put' : 'post';
        await axios[method](url, formData, config);
        fetchItems();
        setIsFormOpen(false);
        setEditingItemId(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this reel?')) {
            const token = localStorage.getItem('app_token');
            await axios.delete(`/api/admin/reels/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchItems();
        }
    };
    
    const handlePublishToYouTube = async (id) => {
        if (!window.confirm('This will upload the reel to your YouTube channel as an "Unlisted" Short. Are you sure you want to proceed?')) return;
        
        setIsPublishing(id); // Set loading state for this specific item
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post(`/api/reels/${id}/publish-youtube`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message || 'Successfully started upload to YouTube! It may take a few minutes to appear on your channel.');
        } catch (error) {
            alert(error.response?.data?.error || 'An unexpected error occurred while publishing.');
        } finally {
            setIsPublishing(null); // Clear loading state
        }
    };

    // Instagram upload logic
    const openInstagramModal = (id, defaultCaption) => {
        setInstaTargetId(id);
        setInstaCaption(defaultCaption || '');
        setShowInstaModal(true);
    };
    const closeInstagramModal = () => {
        setShowInstaModal(false);
        setInstaTargetId(null);
        setInstaCaption('');
    };
    const handleInstagramUpload = async () => {
        setIsUploadingInstagram(instaTargetId);
        setNotification(null);
        try {
            const token = localStorage.getItem('app_token');
            await axios.post(`/api/admin/reels/${instaTargetId}/upload-instagram`, { caption: instaCaption }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotification({ type: 'success', message: 'Instagram upload queued! It may take a few minutes.' });
        } catch (error) {
            setNotification({ type: 'error', message: error.response?.data?.error || 'Failed to upload to Instagram.' });
        } finally {
            setIsUploadingInstagram(null);
            closeInstagramModal();
        }
    };

    if (isLoading) return <p>Loading reels...</p>;

    return (
        <div className="admin-content-manager">
            {isFormOpen && <AdminReelForm reelId={editingItemId} onSave={handleSave} onCancel={() => { setIsFormOpen(false); setEditingItemId(null); }} />}
            <div className="admin-content-header">
                <h3 className="admin-section-title">Manage Reels</h3>
                <button onClick={() => { setEditingItemId(null); setIsFormOpen(true); }} className="btn-primary">Add New Reel</button>
            </div>
            {notification && (
                <div className={`notification ${notification.type}`}>{notification.message}</div>
            )}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Author</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                    <Link to={`/reels/${item.id}`} target="_blank" rel="noopener noreferrer">
                                        {item.title}
                                    </Link>
                                </td>
                                <td>{item.category}</td>
                                <td>{item.author_name}</td>
                                <td className="admin-table-actions">
                                    <button onClick={() => handlePublishToYouTube(item.id)} className="action-btn" title="Publish to YouTube as a Short" disabled={isPublishing === item.id}>
                                        <span className="material-icons" style={{color: '#FF0000'}}>{isPublishing === item.id ? 'hourglass_top' : 'smart_display'}</span>
                                    </button>
                                    <button onClick={() => openInstagramModal(item.id, item.title)} className="action-btn" title="Upload to Instagram" disabled={isUploadingInstagram === item.id}>
                                        <span className="material-icons" style={{color: '#C13584'}}>{isUploadingInstagram === item.id ? 'hourglass_top' : 'photo_camera'}</span>
                                    </button>
                                    <button onClick={() => { setEditingItemId(item.id); setIsFormOpen(true); }} className="action-btn edit"><span className="material-icons">edit</span></button>
                                    <button onClick={() => handleDelete(item.id)} className="action-btn delete"><span className="material-icons">delete_forever</span></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showInstaModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Upload Reel to Instagram</h3>
                        <label>Caption</label>
                        <textarea value={instaCaption} onChange={e => setInstaCaption(e.target.value)} rows={4} style={{width:'100%'}} />
                        <div style={{marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem'}}>
                            <button onClick={closeInstagramModal} className="btn-secondary">Cancel</button>
                            <button onClick={handleInstagramUpload} className="btn-primary" disabled={isUploadingInstagram === instaTargetId}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminReelManager;